import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Badge,
} from "reactstrap";
import { useParams } from "react-router-dom";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import CommonModal from "components/CommonComponents/CommonModal";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import Select from "react-select";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { useDispatch, useSelector } from "react-redux";
import { SELECT_CUSTOM_STYLES, MAX_DOCUMENT_SIZE } from "constants/CommonConstants";
import RightPanelCard from "components/CommonComponents/RightPanelCard";
import { traineeDocuments } from "constants/trainee";
import {
  getTraineeDocumentsByID,
  updateTraineeDocuments,
  uploadTraineeDocuments,
} from "action/trainee";

function TraineeDocuments() {
  const { id } = useParams();
  const decodedId = id ? urlDecodedId(id) : null;

  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const dispatch = useDispatch();
  const { notify } = useContext(NotificationContext);

  // Trainee details for header
  const [traineeInfo, setTraineeInfo] = useState(null);
  const [isUploadPanelOpen, setIsUploadPanelOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [previewData, setPreviewData] = useState({
    src: "",
    width: 0,
    height: 0,
  });
  const [pendingDocs, setPendingDocs] = useState([]);
  const [allDocs, setAllDocs] = useState([]);
  const [uploadDocuments, setUploadDocuments] = useState([
    {
      title: {
        value: null,
        error: "",
      },
      file: {
        value: null,
        error: "",
      },
    },
  ]);
  const [selectedPendingDocs, setSelectedPendingDocs] = useState([]);
  const [showAlternative, setShowAlternative] = useState({
    pending: {
      loader: false,
      noRecords: false,
    },
    documents: {
      loader: false,
      noRecords: false,
    },
    uploadLoader: false,
    error: "",
  });
  const [pagination, setPagination] = useState({
    pendingPage: 1,
    allDocsPage: 1,
  });

  const traineeDataList = useSelector(
    (state) => state.trainees.traineeDocuments,
  );

  const totalUploaded = traineeDataList?.data?.totalUploaded || 0;
  const approvedCount = traineeDataList?.data?.approvedCount || 0;
  const rejectedCount = traineeDataList?.data?.rejectedCount || 0;

  const pendingRecordCount = totalUploaded - (approvedCount + rejectedCount);
  const reviewedRecordCount = approvedCount + rejectedCount;

  const displayPendingDocs = pendingDocs.slice(
    (pagination.pendingPage - 1) * 10,
    pagination.pendingPage * 10,
  );
  const totalPendingPages = Math.ceil(pendingRecordCount / 10);

  const displayAllDocs = allDocs.slice(
    (pagination.allDocsPage - 1) * 10,
    pagination.allDocsPage * 10,
  );
  const totalAllDocsPages = Math.ceil(reviewedRecordCount / 10);

  useEffect(() => {
    if (!decodedId) return;

    setShowAlternative((prev) => ({
      ...prev,
      pending: {
        loader: true,
        noRecords: false,
      },
      documents: {
        loader: true,
        noRecords: false,
      },
      error: "",
    }));

    getTraineeDocumentsByID(dispatch, decodedId);

    return () => {
      dispatch({ type: traineeDocuments, payload: {} });
    };
  }, [dispatch, decodedId]);

  useEffect(() => {
    if (traineeDataList?.code === 200) {
      const data = traineeDataList.data;
      const { pendingApproval, reviewedDocuments } = data;
      // const hasRecords =
      //   (pendingApproval?.length || 0) > 0 ||
      //   (reviewedDocuments?.length || 0) > 0;

      setShowAlternative((prev) => ({
        ...prev,
        pending: {
          loader: false,
          noRecords: (pendingApproval?.length || 0) === 0,
        },
        documents: {
          loader: false,
          noRecords: (reviewedDocuments?.length || 0) === 0,
        },
        error: "",
      }));

      setTraineeInfo({
        fullName: data?.traineeName || "",
        email: data?.email || "—",
        mobile: data?.mobile || "—",
        jointerID: data?.jointerId || "—",
        status: data?.status || "",
        gender: data?.gender || "—",
        city: data?.city?.name || "—",
        state: data?.city?.state?.name || "—",
        profileImage: data?.profileImage || "",
      });

      setPendingDocs(
        pendingApproval
          ? pendingApproval.map((doc) => ({
            id: doc.ID,
            documentId: doc.documentId,
            documentName: doc.documentName,
            fileName: doc.fileName,
            previewUrl: doc.filePath,
            status: doc.status,
            uploadDate: doc.uploadedDate,
            source: doc.uploadedBy || "Trainee",
          }))
          : [],
      );

      setAllDocs(
        reviewedDocuments
          ? reviewedDocuments.map((doc) => ({
            id: doc.ID,
            documentId: doc.documentId,
            documentName: doc.documentName,
            fileName: doc.fileName,
            previewUrl: doc.filePath,
            status: doc.status,
            reason: doc.rejectionReason,
            source: doc.uploadedBy || "Trainee",
            uploadDate: doc.uploadedDate,
          }))
          : [],
      );
    } else if (traineeDataList?.code || traineeDataList?.message) {
      setShowAlternative((prev) => ({
        ...prev,
        pending: {
          ...prev.pending,
          loader: false,
          noRecords: false,
        },
        documents: {
          ...prev.documents,
          loader: false,
          noRecords: false,
        },
        error: traineeDataList?.message || "Failed to load trainee documents.",
      }));
    }
  }, [traineeDataList]);

  const documentOptions = Array.from(
    new Map(
      [
        ...(traineeDataList?.data?.pendingDocuments || []),
        ...(traineeDataList?.data?.pendingApproval || []),
        ...(traineeDataList?.data?.reviewedDocuments || []),
      ].map((doc) => [
        doc.documentId,
        {
          value: doc.documentId,
          label: doc.documentName,
          disabled: [
            ...(traineeDataList?.data?.pendingApproval || []),
            ...(traineeDataList?.data?.reviewedDocuments || []),
          ].some((uploadedDoc) => uploadedDoc.documentId === doc.documentId && uploadedDoc.status !== "REJECTED"),
        },
      ]),
    ).values(),
  );

  const handleSelectAllPending = (e) => {
    if (e.target.checked) {
      setSelectedPendingDocs(pendingDocs.map((doc) => doc.id));
    } else {
      setSelectedPendingDocs([]);
    }
  };

  const handleSelectPending = (id) => {
    if (selectedPendingDocs.includes(id)) {
      setSelectedPendingDocs(
        selectedPendingDocs.filter((docId) => docId !== id),
      );
    } else {
      setSelectedPendingDocs([...selectedPendingDocs, id]);
    }
  };

  // const handleBack = () => {
  //   navigate(
  //     role === "SUPER_ADMIN" ? "/super-admin/trainee" : "/admin/trainee",
  //   );
  // };

  const validateUploadDocuments = () => {
    let updatedDocs = [...uploadDocuments];
    let hasError = false;

    updatedDocs.forEach((doc) => {
      doc.title.error = getValidationError("documentTitle", doc.title.value);

      doc.file.error = getValidationError("documentFile", doc.file.value);

      if (doc.title.error || doc.file.error) {
        hasError = true;
      }
    });

    setUploadDocuments(updatedDocs);

    return !hasError;
  };

  const handleBulkApprove = () => {
    if (selectedPendingDocs.length === 0) return;
    showAlert({
      title: `Approve Documents`,
      message: `Are you sure you want to approve the selected documents?`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const bodyData = {
            status: "APPROVED",
            uploadedDocumentIds: selectedPendingDocs,
          };

          const res = await updateTraineeDocuments(decodedId, bodyData);

          if (res?.code === 200) {
            notify(
              "success",
              res?.message || "Selected documents approved successfully",
            );
            getTraineeDocumentsByID(dispatch, decodedId);
            setSelectedPendingDocs([]);
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
                .join(", ")
              : res?.message || "Failed to approve selected documents";

            notify("danger", message);
          }
        } catch (error) {
          notify("danger", error?.message || "Something went wrong!");
        }
      },
    });
  };

  const getValidationError = (name, value) => {
    // const trimmedValue = typeof value === "string" ? value.trim() : value;

    switch (name) {
      case "documentTitle":
        return !value ? "Please select document title" : "";

      case "documentFile":
        if (!value) return "Please upload document";

        if (
          value.type !== "application/pdf" &&
          !value.type.startsWith("image/")
        ) {
          return "Only PDF and image files are allowed";
        }

        if (value.size > MAX_DOCUMENT_SIZE) {
          return "File size must not exceed 15 MB";
        }

        return "";

      default:
        return "";
    }
  };

  const handleOnBlur = (index, name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);

    setUploadDocuments((prev) => {
      const updated = [...prev];

      if (name === "documentTitle") {
        updated[index].title = {
          value: trimmedValue,
          error,
        };
      }

      if (name === "documentFile") {
        updated[index].file = {
          value: trimmedValue,
          error,
        };
      }

      return updated;
    });
  };

  const handleImagePreview = (e, url) => {
    if (!url) return;
    e.preventDefault();
    e.stopPropagation();
    setPreviewData({
      src: url,
      width: 0,
      height: 0,
    });
  };

  const handleApprove = (docId) => {
    const doc = pendingDocs.find((d) => d.id === docId);
    if (!doc) return;

    showAlert({
      title: `Approve Document`,
      message: `Are you sure you want to approve "${doc.documentName}"?`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const bodyData = {
            status: "APPROVED",
            uploadedDocumentIds: [docId],
          };

          const res = await updateTraineeDocuments(decodedId, bodyData);

          if (res?.code === 200) {
            // notify("success", res?.message || "Document approved successfully");
            notify("success", "Document approved successfully");
            getTraineeDocumentsByID(dispatch, decodedId);
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
                .join(", ")
              : res?.message || "Failed to approve document";

            notify("danger", message);
          }
        } catch (error) {
          notify("danger", error?.message || "Something went wrong!");
        }
      },
    });
  };

  const handleRejectClick = (docId) => {
    const doc = pendingDocs.find((d) => d.id === docId);
    if (!doc) return;

    showAlert({
      title: `Reject Document`,
      message: `Are you sure you want to reject "${doc.documentName}"?`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      showInput: true,
      inputPlaceholder: "Reason for rejection",
      inputRequired: true,
      inputErrorMsg: "Please enter reason for rejection",
      onConfirm: async (reason) => {
        try {
          const bodyData = {
            status: "REJECTED",
            documentId: docId,
            remark: reason,
          };

          const res = await updateTraineeDocuments(decodedId, bodyData);

          if (res?.code === 200) {
            // notify("success", res?.message || "Document rejected successfully");
            notify("success", "Document rejected successfully");
            getTraineeDocumentsByID(dispatch, decodedId);
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
                .join(", ")
              : res?.message || "Failed to reject document";

            notify("danger", message);
          }
        } catch (error) {
          notify("danger", error?.message || "Something went wrong!");
        }
      },
    });
  };

  const handleUpload = async () => {
    const isValid = validateUploadDocuments();
    if (!isValid) return;

    const formData = new FormData();
    let hasFiles = false;

    uploadDocuments.forEach((doc) => {
      if (doc.file.value && doc.title.value) {
        formData.append("documentId", doc.title.value.value);
        formData.append("document", doc.file.value);
        hasFiles = true;
      }
    });

    if (!hasFiles) return;

    try {
      setShowAlternative((prev) => ({
        ...prev,
        uploadLoader: true,
      }));

      const res = await uploadTraineeDocuments(decodedId, formData);

      if (res?.code === 200 || res?.status === "SUCCESS") {
        notify("success", res?.message || "Documents uploaded successfully");
        getTraineeDocumentsByID(dispatch, decodedId);
        setIsUploadPanelOpen(false);
        setUploadDocuments([
          {
            title: { value: null, error: "" },
            file: { value: null, error: "" },
          },
        ]);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
            .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
            .join(", ")
          : res?.message || "Failed to upload documents";

        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Something went wrong!");
    } finally {
      setShowAlternative((prev) => ({
        ...prev,
        uploadLoader: false,
      }));
    }
  };

  // const handleDeleteClick = (docId) => {
  //   const doc = allDocs.find((d) => d.id === docId);
  //   if (!doc) return;

  //   showAlert({
  //     title: `Delete Document`,
  //     message: `Are you sure you want to delete "${doc.documentName}"?`,
  //     type: "warning",
  //     confirmStyle: "deep-blue",
  //     cancelStyle: "secondary",
  //     confirmText: "Delete",
  //     cancelText: "Cancel",
  //     showInput: true,
  //     inputPlaceholder: "Reason for deletion",
  //     inputRequired: true,
  //     inputErrorMsg: "Please enter reason for deletion",
  //     onConfirm: async (reason) => {
  //       try {
  //         const bodyData = {
  //           remark: reason,
  //         };
  //         const res = await deleteTraineeDocument(docId, bodyData);
  //         if (res?.code === 200) {
  //           // notify("success", res?.message || "Document deleted successfully");
  //           notify("success", "Document deleted successfully");
  //           getTraineeDocumentsByID(dispatch, decodedId);
  //         } else {
  //           const message = Array.isArray(res?.message)
  //             ? res.message
  //                 .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
  //                 .join(", ")
  //             : res?.message || "Failed to delete document";

  //           notify("danger", message);
  //         }
  //       } catch (error) {
  //         notify("danger", error?.message || "Something went wrong!");
  //       }
  //     },
  //   });
  // };

  const isDocumentDisabled = (option, currentIndex) =>
    option.disabled ||
    uploadDocuments.some(
      (doc, index) =>
        index !== currentIndex && doc.title.value?.value === option.value,
    );

  const pendingColumns = [
    {
      Header: () => (
        <Input
          type="checkbox"
          className="ml-3 mt-0 custom-wrapper"
          checked={
            selectedPendingDocs.length === pendingDocs.length &&
            pendingDocs.length > 0
          }
          onChange={handleSelectAllPending}
        />
      ),
      accessor: "selection",
      disableSortBy: true,
      minWidth: 50,
      maxWidth: 50,
      Cell: ({ row }) => {
        const id = row.original.id;
        return (
          <Input
            type="checkbox"
            className="ml-3 mt-0 custom-wrapper"
            checked={selectedPendingDocs.includes(id)}
            onChange={() => handleSelectPending(id)}
          />
        );
      },
    },
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 60,
      maxWidth: 60,
      centerAlign: true,
      Cell: ({ row }) => (
        <span>{(pagination.pendingPage - 1) * 10 + row.index + 1}</span>
      ),
    },
    {
      Header: "Document Name",
      accessor: "documentName",
      minWidth: 150,
      maxWidth: 250,
      Cell: ({ value }) => <span className="text-wrapper">{value || "—"}</span>,
    },
    {
      Header: "Upload Date",
      accessor: "uploadDate",
      Cell: ({ value }) => <span>{value || "—"}</span>,
    },
    {
      Header: "File Preview",
      accessor: "fileName",
      minWidth: 100,
      maxWidth: 100,
      Cell: ({ row }) => {
        const url = row.original.previewUrl;
        return (
          <div className="d-flex">
            {url ? (
              <Button
                className="btn-link p-0 text-primary"
                size="sm"
                onClick={(e) => handleImagePreview(e, url)}
                title="Preview Document"
              >
                <i className="fas fa-eye" /> Preview
              </Button>
            ) : (
              <span>—</span>
            )}
          </div>
        );
      },
    },

    ...(role !== "CHANNEL_PARTNER"
      ? [
        {
          Header: "Actions",
          disableSortBy: true,
          Cell: ({ row }) => (
            <div className="d-flex align-items-center">
              <Button
                color="link"
                size="sm"
                className="p-0 mr-3 text-success text-decoration-none shadow-none"
                onClick={() => handleApprove(row.original.id)}
                title="Approve"
              >
                <i className="fa fa-check text-success mr-1" /> Approve
              </Button>

              <Button
                color="link"
                size="sm"
                className="p-0 text-danger text-decoration-none shadow-none"
                onClick={() => handleRejectClick(row.original.id)}
                title="Reject"
              >
                <i className="fa fa-times text-danger mr-1" /> Reject
              </Button>
            </div>
          ),
        },
      ]
      : []),
  ];

  const allDocsColumns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 100,
      maxWidth: 100,
      centerAlign: true,
      Cell: ({ row }) => (
        <span>{(pagination.allDocsPage - 1) * 10 + row.index + 1}</span>
      ),
    },
    {
      Header: "Document Name",
      accessor: "documentName",
      minWidth: 150,
      maxWidth: 250,
      Cell: ({ value }) => <span className="text-wrapper">{value || "—"}</span>,
    },
    {
      Header: "Source",
      accessor: "source",
      minWidth: 130,
      maxWidth: 130,
      Cell: ({ value }) => <span>{value || "—"}</span>,
    },
    {
      Header: "Upload Date",
      accessor: "uploadDate",
      minWidth: 130,
      maxWidth: 130,
      Cell: ({ value }) => <span>{value || "—"}</span>,
    },
    {
      Header: "Status",
      accessor: "status",
      centerAlign: true,
      Cell: ({ value }) => (
        <Badge
          color={
            value === "APPROVED"
              ? "success"
              : value === "REJECTED"
                ? "warning"
                : "danger"
          }
          pill
        >
          {value || "—"}
        </Badge>
      ),
    },
    {
      Header: "Rejection Reason",
      accessor: "reason",
      minWidth: 130,
      maxWidth: 130,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "File Preview",
      accessor: "fileName",
      minWidth: 110,
      maxWidth: 110,
      Cell: ({ row }) => {
        const url = row.original.previewUrl;
        return (
          <div className="d-flex">
            {url ? (
              <Button
                className="btn-link p-0 text-primary"
                size="sm"
                onClick={(e) => handleImagePreview(e, url)}
                title="Preview Document"
              >
                <i className="fas fa-eye" /> Preview
              </Button>
            ) : (
              <span>—</span>
            )}
          </div>
        );
      },
    },
    // {
    //   Header: "Actions",
    //   disableSortBy: true,
    //   Cell: ({ row }) => {
    //     const id = row.original.id;
    //     return (
    //       <div className="d-flex">
    //         <Button
    //           className="btn-link d-flex align-items-center p-0"
    //           id={`delete${id}`}
    //           size="sm"
    //           onClick={(e) => {
    //             e.stopPropagation();
    //             handleDeleteClick(id);
    //           }}
    //         >
    //           <i className="fas fa-trash mx-1 table-action-delete" /> Delete
    //         </Button>
    //         <UncontrolledTooltip delay={0} target={`delete${id}`}>
    //           Delete
    //         </UncontrolledTooltip>
    //       </div>
    //     );
    //   },
    // },
  ];

  return (
    <>
      <div>
        {/* SECTION 1: PENDING DOCUMENTS */}
        <Card className="section-cards mb-4">
          <CardHeader className="section-header td-card-header">
            <h4 className="mb-0 text-default section-title">
              Pending Approval
              <span className="total-records ml-2">
                (Total Records: {pendingRecordCount})
              </span>
            </h4>

            {selectedPendingDocs.length > 0 && (
              <div className="td-card-header-actions">
                <Button color="deep-blue" onClick={handleBulkApprove}>
                  <i className="fa fa-check mr-1" /> Approve Selected
                </Button>
              </div>
            )}
          </CardHeader>
          <CardBody className="p-0">
            {showAlternative.pending.loader ? (
              <div className="loaderContainer-sm">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : showAlternative.error ? (
              <div className="loaderContainer-sm error-style">
                <div className="error-icon">
                  <i className="fa-solid fa-circle-exclamation"></i>
                </div>
                <span>{showAlternative.error}</span>
              </div>
            ) : showAlternative.pending.noRecords ? (
              <div className="loaderContainer-sm empty-style">
                <div className="empty-icon">
                  <i className="fa-solid fa-circle-info"></i>
                </div>
                <span>No pending documents found.</span>
              </div>
            ) : (
              <div className="table-responsive">
                <CommonTable
                  columns={pendingColumns}
                  data={displayPendingDocs}
                  showPaginationBottom={true}
                  activePage={pagination.pendingPage}
                  setPageNum={(page) =>
                    setPagination((prev) => ({ ...prev, pendingPage: page }))
                  }
                  totalPages={totalPendingPages}
                />
              </div>
            )}
          </CardBody>
        </Card>

        {/* SECTION 3: ALL DOCUMENTS */}
        <Card className="section-cards mb-4">
          <CardHeader className="section-header td-card-header">
            <h4 className="mb-0 text-default section-title">
              Documents
              <span className="total-records ml-2">
                (Total Records: {reviewedRecordCount})
              </span>
            </h4>
            <div className="td-card-header-actions">
              <Button
                className="btn-deep-blue"
                onClick={() => setIsUploadPanelOpen(true)}
              >
                <i className="fa fa-upload mr-1" />
                Upload Documents
              </Button>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {showAlternative.documents.loader ? (
              <div className="loaderContainer-sm">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : showAlternative.error ? (
              <div className="loaderContainer-sm error-style">
                <div className="error-icon">
                  <i className="fa-solid fa-circle-exclamation"></i>
                </div>
                <span>{showAlternative.error}</span>
              </div>
            ) : showAlternative.documents.noRecords ? (
              <div className="loaderContainer-sm empty-style">
                <div className="empty-icon">
                  <i className="fa-solid fa-circle-info"></i>
                </div>
                <span>No documents found.</span>
              </div>
            ) : (
              <div className="table-responsive">
                <CommonTable
                  columns={allDocsColumns}
                  data={displayAllDocs}
                  showPaginationBottom={true}
                  activePage={pagination.allDocsPage}
                  setPageNum={(page) =>
                    setPagination((prev) => ({ ...prev, allDocsPage: page }))
                  }
                  totalPages={totalAllDocsPages}
                />
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <CommonModal
        isOpen={!!previewData.src}
        toggle={() =>
          setPreviewData({
            src: "",
            width: 0,
            height: 0,
          })
        }
        title="Document Preview"
        size="xl"
        centered={true}
      >
        {previewData.src &&
          (previewData.src.toLowerCase().includes(".pdf") ? (
            <div className="document-pdf-preview">
              <iframe
                src={previewData.src}
                title="Document Preview"
                className="document-pdf-frame"
              />
            </div>
          ) : (
            <div className="text-center">
              <img
                src={previewData.src}
                alt="Document Preview"
                className="img-fluid modal-preview-media"
              />
            </div>
          ))}
      </CommonModal>

      <RightPanelCard
        isOpen={isUploadPanelOpen}
        enableScroll={true}
        onClose={() => {
          setIsUploadPanelOpen(false);
          setUploadDocuments([
            {
              title: { value: null, error: "" },
              file: { value: null, error: "" },
            },
          ]);
        }}
      >
        <Card className="h-100 d-flex flex-column">
          <CardHeader tag="div" className="p-3 flex-shrink-0">
            <div className="d-flex align-items-center justify-content-between mt-2">
              <div className="d-flex align-items-center">
                <i
                  className="fa-solid fa-angle-left cursor mr-2"
                  onClick={() => setIsUploadPanelOpen(false)}
                />

                <div className="d-flex align-items-center">
                  <h4 className="mb-0 mr-2 text-default">Upload Documents</h4>

                  <span className="td-upload-format-hint mb-1">
                    ( Accepted formats: PDF, JPG, JPEG, PNG - Max 15 MB )
                  </span>
                </div>
              </div>

              <i
                className="fa-solid fa-xmark cursor text-muted"
                onClick={() => setIsUploadPanelOpen(false)}
              />
            </div>
          </CardHeader>

          <CardBody className="td-upload-card-body">
            <div className="document-section w-100">
              {uploadDocuments.map((doc, index) => {
                const totalDocs = uploadDocuments.length;

                const sortedOptions = [
                  ...documentOptions.filter(
                    (option) => !isDocumentDisabled(option, index),
                  ),
                  ...documentOptions.filter((option) =>
                    isDocumentDisabled(option, index),
                  ),
                ];

                return (
                  <div key={index} className="td-upload-row">
                    {/* Sr. No. */}
                    <div className="td-upload-serial mt-1">
                      <span className="td-upload-serial-num">{index + 1}</span>
                    </div>

                    {/* Document Title */}
                    <div className="td-upload-field">
                      <FormGroup className="mb-0">
                        <Label className="td-field-label">
                          Document Type <span className="text-danger">*</span>
                        </Label>
                        <Select
                          options={sortedOptions}
                          isOptionDisabled={(option) =>
                            isDocumentDisabled(option, index)
                          }
                          placeholder="Select document title"
                          value={doc.title.value}
                          onChange={(selected) => {
                            const updatedDocs = [...uploadDocuments];
                            updatedDocs[index].title = {
                              value: selected,
                              error: "",
                            };
                            setUploadDocuments(updatedDocs);
                          }}
                          onBlur={() =>
                            handleOnBlur(
                              index,
                              "documentTitle",
                              doc.title.value,
                            )
                          }
                          styles={SELECT_CUSTOM_STYLES}
                          classNamePrefix="react-select"
                          className={doc.title.error ? "is-invalid" : ""}
                          menuPortalTarget={document.body}
                          menuPosition="absolute"
                        />
                        {doc.title.error && (
                          <div className="invalid-feedback d-block">
                            {doc.title.error}
                          </div>
                        )}
                      </FormGroup>
                    </div>

                    {/* Upload File */}
                    <div className="td-upload-field">
                      <FormGroup className="mb-0">
                        <Label className="td-field-label">
                          Upload Document <span className="text-danger">*</span>
                        </Label>
                        <div
                          className={`td-file-picker ${
                            doc.file.error ? "td-file-picker--invalid" : ""
                          }`}
                        >
                          <Label className="td-browse-btn mb-0">
                            <i className="fas fa-folder-open mr-1" /> Browse
                            <Input
                              type="file"
                              hidden
                              accept="application/pdf,image/*"
                              id={`panelDocumentFile-${index}`}
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;

                                const error = getValidationError(
                                  "documentFile",
                                  file,
                                );

                                const updatedDocs = [...uploadDocuments];
                                updatedDocs[index].file = {
                                  value: error ? null : file,
                                  error: error,
                                };
                                setUploadDocuments(updatedDocs);
                                e.target.value = "";
                              }}
                              onBlur={() =>
                                handleOnBlur(
                                  index,
                                  "documentFile",
                                  doc.file.value,
                                )
                              }
                            />
                          </Label>
                          <div className="td-file-info">
                            <i className="fa fa-file-pdf-o td-file-pdf-icon" />
                            <span
                              className={`td-file-name ${!doc.file.value ? "td-file-name--placeholder" : ""}`}
                            >
                              {doc.file.value?.name || "No file chosen"}
                            </span>
                            {doc.file.value && (
                              <i
                                className="fa fa-times td-file-remove"
                                onClick={() => {
                                  const updatedDocs = [...uploadDocuments];
                                  updatedDocs[index].file = {
                                    value: null,
                                    error: "",
                                  };
                                  setUploadDocuments(updatedDocs);
                                }}
                                title="Remove file"
                              />
                            )}
                          </div>
                        </div>
                        {doc.file.error && (
                          <div className="invalid-feedback d-block">
                            {doc.file.error}
                          </div>
                        )}
                      </FormGroup>
                    </div>

                    {/* Add / Remove row buttons */}
                    <div className="td-upload-actions">
                      {totalDocs > 1 && (
                        <button
                          className="td-row-action-btn td-row-action-btn--remove mt-1"
                          onClick={() =>
                            setUploadDocuments(
                              uploadDocuments.filter((_, i) => i !== index),
                            )
                          }
                          title="Remove row"
                        >
                          <i className="fa fa-times" />
                        </button>
                      )}
                      {index === totalDocs - 1 &&
                        totalDocs < documentOptions.length && (
                          <button
                            className="td-row-action-btn td-row-action-btn--add mt-1"
                            onClick={() =>
                              setUploadDocuments([
                                ...uploadDocuments,
                                {
                                  title: { value: "", error: "" },
                                  file: { value: null, error: "" },
                                },
                              ])
                            }
                            title="Add another document"
                          >
                            <i className="fa fa-plus" />
                          </button>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardBody>

          <CardFooter className="text-right flex-shrink-0">
            <Button
              color="secondary"
              className="save-btn-sm mr-2"
              onClick={() => {
                setIsUploadPanelOpen(false);
                setUploadDocuments([
                  {
                    title: { value: null, error: "" },
                    file: { value: null, error: "" },
                  },
                ]);
              }}
            >
              Cancel
            </Button>
            <Button
              color="deep-blue"
              className="save-btn-sm"
              onClick={handleUpload}
              disabled={showAlternative.uploadLoader}
            >
              {showAlternative.uploadLoader ? (
                <>
                  <i className="fa fa-spinner fa-spin mr-2" />
                </>
              ) : (
                "Upload"
              )}
            </Button>
          </CardFooter>
        </Card>
      </RightPanelCard>

      {/* Profile Image Modal */}
      {showProfileModal && traineeInfo?.profileImage && (
        <div
          className="image-modal-overlay"
          onClick={() => setShowProfileModal(false)}
        >
          <div className="image-modal-content">
            <img src={traineeInfo.profileImage} alt="profile-large" />
          </div>
        </div>
      )}
    </>
  );
}

export default TraineeDocuments;
